/**
 * Pedidos de clientes: totales por pedido y resumen del dia de entrega.
 *
 * CODIGO PURO. No conoce la base de datos.
 *
 * REGLA CENTRAL, y la razon de que este modulo no toque nada mas:
 *
 *   El total de un pedido es lo que el cliente DEBE, no ingreso del negocio.
 *
 * El ingreso nace unicamente al registrar una venta, y el costo se congela al
 * completar una produccion. Si los totales de pedidos se sumaran como ingreso,
 * habria dos cifras distintas peleandose por el mismo dinero y las ganancias
 * dejarian de cuadrar. Por eso aqui se habla de "cobrado" y "por cobrar", nunca
 * de ingreso.
 *
 * Un pedido cancelado conserva su registro pero NO suma en ningun total: se
 * cuenta aparte para que se pueda ver que existio.
 */

import { ErrorValidacion } from './errors';
import type { Centavos } from './money';
import type { FechaLocal } from './dates';

// ---------------------------------------------------------------------------
// Estados
// ---------------------------------------------------------------------------

/**
 * Cancelado en lugar de borrado.
 *
 * Cuando el cliente se arrepiente, el pedido existio y conviene poder verlo.
 * Borrar se reserva para el error de captura, que es otra cosa.
 */
export type EstadoPedido = 'activo' | 'cancelado';
export type EstadoPago = 'pendiente' | 'pagado';
export type EstadoEntrega = 'pendiente' | 'entregado';

export const ESTADOS_PEDIDO: Readonly<Record<EstadoPedido, string>> = {
  activo: 'Activo',
  cancelado: 'Cancelado',
};

export const ESTADOS_PAGO: Readonly<Record<EstadoPago, string>> = {
  pendiente: 'Pendiente',
  pagado: 'Pagado',
};

export const ESTADOS_ENTREGA: Readonly<Record<EstadoEntrega, string>> = {
  pendiente: 'Pendiente',
  entregado: 'Entregado',
};

// ---------------------------------------------------------------------------
// Total de un pedido
// ---------------------------------------------------------------------------

/**
 * Porciones por precio, redondeado una sola vez al final.
 *
 * El resultado se guarda en el pedido en lugar de recalcularse al leer, para
 * que subir el precio del platillo manana no cambie lo que este cliente debe.
 */
export function totalDelPedido(porciones: number, precioPorPorcionCentavos: Centavos): Centavos {
  if (!Number.isFinite(porciones) || porciones <= 0) {
    throw new ErrorValidacion('Las porciones del pedido deben ser mayores que cero.', 'porciones');
  }
  if (!Number.isFinite(precioPorPorcionCentavos) || precioPorPorcionCentavos < 0) {
    throw new ErrorValidacion('El precio del pedido no puede ser negativo.', 'precio');
  }
  return Math.round(porciones * precioPorPorcionCentavos);
}

// ---------------------------------------------------------------------------
// Resumen de un conjunto de pedidos
// ---------------------------------------------------------------------------

/** Lo minimo que necesita este modulo de un pedido. */
export interface PedidoParaResumen {
  porciones: number;
  totalCentavos: Centavos;
  estado: EstadoPedido;
  estadoPago: EstadoPago;
  estadoEntrega: EstadoEntrega;
}

export interface ResumenPedidos {
  /** Pedidos activos. Los cancelados se cuentan aparte. */
  cuantos: number;
  porciones: number;
  totalCentavos: Centavos;
  /** Lo que ya te pagaron. */
  cobradoCentavos: Centavos;
  /** Lo que te deben. Siempre `totalCentavos - cobradoCentavos`. */
  porCobrarCentavos: Centavos;
  entregados: number;
  porEntregar: number;
  cancelados: number;
}

export function resumenDePedidos(
  pedidos: readonly PedidoParaResumen[],
): ResumenPedidos {
  const resumen: ResumenPedidos = {
    cuantos: 0,
    porciones: 0,
    totalCentavos: 0,
    cobradoCentavos: 0,
    porCobrarCentavos: 0,
    entregados: 0,
    porEntregar: 0,
    cancelados: 0,
  };

  for (const pedido of pedidos) {
    if (pedido.estado === 'cancelado') {
      resumen.cancelados += 1;
      continue;
    }

    resumen.cuantos += 1;
    resumen.porciones += pedido.porciones;
    resumen.totalCentavos += pedido.totalCentavos;

    if (pedido.estadoPago === 'pagado') resumen.cobradoCentavos += pedido.totalCentavos;
    if (pedido.estadoEntrega === 'entregado') resumen.entregados += 1;
    else resumen.porEntregar += 1;
  }

  // Por resta, no sumando los pendientes por separado: asi lo cobrado y lo por
  // cobrar siempre dan el total exacto, sin que un redondeo deje sobras.
  resumen.porCobrarCentavos = resumen.totalCentavos - resumen.cobradoCentavos;

  return resumen;
}

// ---------------------------------------------------------------------------
// Agrupacion por dia de entrega
// ---------------------------------------------------------------------------

export interface PedidoConFecha extends PedidoParaResumen {
  fechaEntrega: FechaLocal;
}

export interface DiaDePedidos<T> {
  fecha: FechaLocal;
  pedidos: T[];
  resumen: ResumenPedidos;
}

/**
 * Agrupa los pedidos por dia de entrega, del mas proximo al mas lejano.
 *
 * La fecha de entrega es el ancla del modulo, igual que la fecha de venta lo es
 * del menu: es la pregunta que se hace por la manana. Se ordena ascendente
 * porque el dia que importa es el que viene, no el ultimo capturado.
 *
 * Dentro de cada dia se conserva el orden en que llegaron los pedidos.
 */
export function agruparPorFechaEntrega<T extends PedidoConFecha>(
  pedidos: readonly T[],
): Array<DiaDePedidos<T>> {
  const porFecha = new Map<FechaLocal, T[]>();

  for (const pedido of pedidos) {
    const lista = porFecha.get(pedido.fechaEntrega);
    if (lista === undefined) porFecha.set(pedido.fechaEntrega, [pedido]);
    else lista.push(pedido);
  }

  return [...porFecha.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, lista]) => ({
      fecha,
      pedidos: lista,
      resumen: resumenDePedidos(lista),
    }));
}

// ---------------------------------------------------------------------------
// Vista de Pedidos: primero lo que falta cobrar
// ---------------------------------------------------------------------------

export interface PedidosPorCobrar<T> {
  /** Activos sin pagar. Son la razon de ser de la vista administrativa. */
  pendientes: T[];
  /** Activos ya pagados. Se siguen mostrando: sirven para consultar de quien es cada comida. */
  pagados: T[];
  /** Cancelados. Ni se deben ni se cobraron, van al final. */
  cancelados: T[];
}

/**
 * Separa los pedidos de un dia en los tres bloques de la vista de Pedidos.
 *
 * Se separa en lugar de solo ordenar para que la pantalla pueda encabezar cada
 * bloque sin adivinar donde termina uno y empieza el otro.
 *
 * Dentro de cada bloque se conserva el orden en que venian, que es el de
 * captura: mover un pedido de bloque solo debe pasar cuando cambia su estado, no
 * porque se releyo la lista.
 */
export function separarPorCobrar<T extends PedidoParaResumen>(
  pedidos: readonly T[],
): PedidosPorCobrar<T> {
  const resultado: PedidosPorCobrar<T> = { pendientes: [], pagados: [], cancelados: [] };

  for (const pedido of pedidos) {
    if (pedido.estado === 'cancelado') resultado.cancelados.push(pedido);
    else if (pedido.estadoPago === 'pagado') resultado.pagados.push(pedido);
    else resultado.pendientes.push(pedido);
  }

  return resultado;
}

// ---------------------------------------------------------------------------
// Vista de Entregas: agrupada por platillo
// ---------------------------------------------------------------------------

/**
 * Lo minimo para armar la vista de entregas.
 *
 * `nombreReceta` lo entrega quien lee la base de datos: este modulo no consulta
 * recetas, solo ordena y agrupa con el nombre que le pasan.
 */
export interface PedidoParaEntrega extends PedidoParaResumen {
  recipeId: string;
  nombreReceta: string;
}

export interface PlatilloDeEntrega<T> {
  recipeId: string;
  nombreReceta: string;
  /** Porciones de todos los pedidos activos de este platillo ese dia. */
  porciones: number;
  porcionesPorEntregar: number;
  porcionesEntregadas: number;
  /** true cuando ya no falta ninguna porcion por entregar. */
  completo: boolean;
  /** Pendientes primero, entregados despues. */
  pedidos: T[];
}

/**
 * Agrupa los pedidos de un dia por platillo, para servir y rotular.
 *
 * Es la otra pregunta del modulo, distinta de la de cobranza: cuando cuatro
 * personas pidieron lo mismo, hay que saber de quien es cada porcion para poner
 * el nombre en cada recipiente.
 *
 * Reglas de esta vista:
 *
 *   - Los CANCELADOS no aparecen. No se rotula un recipiente de algo que se
 *     cancelo. Siguen visibles en la vista de Pedidos, que es la que lleva el
 *     registro.
 *   - Se agrupa por `recipeId`, no por nombre: dos recetas podrian llamarse
 *     igual, y una receta borrada no debe fundirse con otra.
 *   - Lo que ya se entrego baja al final, tanto el cliente dentro de su platillo
 *     como el platillo completo cuando ya no le falta nada. Arriba queda
 *     siempre lo que falta.
 */
export function agruparPorPlatillo<T extends PedidoParaEntrega>(
  pedidos: readonly T[],
): Array<PlatilloDeEntrega<T>> {
  const porReceta = new Map<string, PlatilloDeEntrega<T>>();

  for (const pedido of pedidos) {
    if (pedido.estado === 'cancelado') continue;

    let grupo = porReceta.get(pedido.recipeId);
    if (grupo === undefined) {
      grupo = {
        recipeId: pedido.recipeId,
        nombreReceta: pedido.nombreReceta,
        porciones: 0,
        porcionesPorEntregar: 0,
        porcionesEntregadas: 0,
        completo: true,
        pedidos: [],
      };
      porReceta.set(pedido.recipeId, grupo);
    }

    grupo.porciones += pedido.porciones;
    if (pedido.estadoEntrega === 'entregado') grupo.porcionesEntregadas += pedido.porciones;
    else grupo.porcionesPorEntregar += pedido.porciones;
    grupo.pedidos.push(pedido);
  }

  const grupos = [...porReceta.values()];

  for (const grupo of grupos) {
    grupo.completo = grupo.porcionesPorEntregar === 0;
    grupo.pedidos = ordenarPendientesPrimero(grupo.pedidos);
  }

  // Los platillos que ya no deben nada bajan al final. Dentro de cada bloque, por
  // nombre, para que la posicion no dependa del orden de captura.
  return grupos.sort(
    (a, b) =>
      Number(a.completo) - Number(b.completo) ||
      a.nombreReceta.localeCompare(b.nombreReceta, 'es'),
  );
}

/** Pendientes primero, conservando el orden de captura dentro de cada bloque. */
function ordenarPendientesPrimero<T extends PedidoParaResumen>(pedidos: readonly T[]): T[] {
  const pendientes: T[] = [];
  const entregados: T[] = [];

  for (const pedido of pedidos) {
    if (pedido.estadoEntrega === 'entregado') entregados.push(pedido);
    else pendientes.push(pedido);
  }

  return [...pendientes, ...entregados];
}

/**
 * Totales del dia para la vista de Entregas, contados en PORCIONES.
 *
 * Se cuenta en porciones y no en pedidos porque lo que se sirve son porciones:
 * un pedido de tres porciones son tres recipientes. Y sin importes: el dinero es
 * asunto de la vista de Pedidos.
 */
export interface ResumenEntregas {
  porciones: number;
  porcionesPorEntregar: number;
  porcionesEntregadas: number;
  /** Cuantos platillos distintos hay que preparar. */
  platillos: number;
}

export function resumenDeEntregas(
  pedidos: readonly PedidoParaEntrega[],
): ResumenEntregas {
  const resumen: ResumenEntregas = {
    porciones: 0,
    porcionesPorEntregar: 0,
    porcionesEntregadas: 0,
    platillos: 0,
  };
  const recetas = new Set<string>();

  for (const pedido of pedidos) {
    if (pedido.estado === 'cancelado') continue;

    recetas.add(pedido.recipeId);
    resumen.porciones += pedido.porciones;
    if (pedido.estadoEntrega === 'entregado') resumen.porcionesEntregadas += pedido.porciones;
    else resumen.porcionesPorEntregar += pedido.porciones;
  }

  resumen.platillos = recetas.size;
  return resumen;
}

// ---------------------------------------------------------------------------
// Vista de Cuentas: quien me debe y cuanto
// ---------------------------------------------------------------------------

export interface PedidoParaCuenta extends PedidoConFecha {
  clienteNombre: string;
}

// ---------------------------------------------------------------------------
// El nombre del cliente: una sola regla para reconocerlo
// ---------------------------------------------------------------------------

/**
 * Clave con la que se reconoce a una persona, sin importar como se escribio.
 *
 * EL PROBLEMA QUE RESUELVE
 *
 * No hay catalogo de clientes: el nombre se escribe a mano en cada pedido. Escrito
 * "Juan" un dia y "juan" otro, las cuentas por cobrar lo tomaban como DOS personas
 * y ninguna con la deuda completa. Con nombres que llevan acento, como Toño, la
 * probabilidad de escribirlo distinto es todavia mayor.
 *
 * Se ignoran mayusculas, acentos y espacios de sobra, que son las tres formas en
 * que la misma persona se escribe distinto al capturar rapido en el telefono.
 *
 * Esta es la UNICA regla de comparacion de nombres del proyecto. Tener dos reglas
 * distintas es exactamente lo que produjo el error: el precio por cliente ya
 * ignoraba mayusculas mientras las cuentas comparaban exacto.
 */
export function claveDeCliente(nombre: string): string {
  return nombre
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** "juan carlos" -> "Juan Carlos". Cada palabra con su inicial en mayuscula. */
function aFormaDeNombre(texto: string): string {
  return texto
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((palabra) =>
      palabra === '' ? palabra : palabra[0]?.toUpperCase() + palabra.slice(1).toLowerCase(),
    )
    .join(' ');
}

/** true para "Juan" y "Ana María"; false para "juan", "JUAN" y "JuAn". */
function tieneFormaDeNombre(texto: string): boolean {
  return texto.trim() !== '' && texto.trim() === aFormaDeNombre(texto);
}

/**
 * Elige como se MUESTRA una persona, entre todas las formas en que se escribio.
 *
 * Se prefiere una escritura que ella haya usado de verdad, y solo se corrige la
 * presentacion cuando ninguna la tiene. Eso importa con los acentos: si escribio
 * "Toño", se muestra "Toño" y no "Tono". Inventar la escritura de un nombre propio
 * es peor que respetar la que uso.
 *
 * Orden de preferencia:
 *   1. La escritura mas usada de las que ya tienen forma de nombre propio.
 *   2. Si ninguna la tiene, la mas usada, con la inicial corregida.
 *
 * A igual uso gana la primera en orden alfabetico, para que el resultado no
 * dependa del orden en que se recorrieron los pedidos.
 */
export function nombreParaMostrar(escrituras: readonly string[]): string {
  const conteo = new Map<string, number>();
  for (const escritura of escrituras) {
    const limpia = escritura.trim().replace(/\s+/g, ' ');
    if (limpia === '') continue;
    conteo.set(limpia, (conteo.get(limpia) ?? 0) + 1);
  }
  if (conteo.size === 0) return '';

  const masUsada = (candidatas: Array<[string, number]>): string =>
    candidatas.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'es'))[0]?.[0] ?? '';

  const conForma = [...conteo.entries()].filter(([texto]) => tieneFormaDeNombre(texto));
  if (conForma.length > 0) return masUsada(conForma);

  return aFormaDeNombre(masUsada([...conteo.entries()]));
}

export interface CuentaDeCliente<T> {
  clienteNombre: string;
  /** Lo que debe: suma de sus pedidos activos sin pagar. */
  totalCentavos: Centavos;
  cuantos: number;
  porciones: number;
  /** Entrega mas antigua con adeudo. Es la deuda que lleva mas tiempo esperando. */
  desde: FechaLocal;
  hasta: FechaLocal;
  /** De esos pedidos sin pagar, cuantos ya se entregaron. */
  entregados: number;
  /** Del mas viejo al mas reciente. */
  pedidos: T[];
}

export interface CuentasPorCobrar<T> {
  /** De la deuda mas grande a la mas chica. */
  clientes: Array<CuentaDeCliente<T>>;
  totalCentavos: Centavos;
  cuantos: number;
}

/**
 * Agrupa por cliente lo que falta cobrar.
 *
 * Es la tercera pregunta del modulo, distinta de las otras dos:
 *
 *   pedidos  = que pedi para este dia
 *   entregas = para quien es cada plato
 *   cuentas  = quien me debe y cuanto
 *
 * NO depende del dia. La deuda es acumulada: la de una persona puede venir de
 * ocho pedidos de ocho dias distintos, y sumarla a mano es justo lo que esta
 * vista evita.
 *
 * Reglas:
 *
 *   - Solo cuentan los pedidos ACTIVOS y SIN PAGAR. Un cancelado no se cobra y
 *     uno pagado ya no debe nada.
 *   - Los clientes van de la deuda mas grande a la mas chica, porque es el orden
 *     en que conviene cobrar. A igual monto, por nombre.
 *   - Dentro de cada cliente, del pedido mas viejo al mas nuevo: el que lleva
 *     mas tiempo sin pagarse es el que hay que mencionar primero.
 *
 * Ojo con lo que este total NO es: no es ingreso. El ingreso nace al registrar la
 * venta. Esto es cuentas por cobrar.
 */
export function cuentasPorCobrar<T extends PedidoParaCuenta>(
  pedidos: readonly T[],
): CuentasPorCobrar<T> {
  const porCliente = new Map<string, CuentaDeCliente<T>>();

  /**
   * Todas las formas en que se escribio cada persona, para decidir al final como
   * se muestra. Se junta aparte porque la decision necesita verlas todas.
   */
  const escrituras = new Map<string, string[]>();

  for (const pedido of pedidos) {
    if (pedido.estado === 'cancelado') continue;
    if (pedido.estadoPago === 'pagado') continue;

    // "Juan", "juan" y "JUAN" son la misma persona y su deuda se suma.
    const clave = claveDeCliente(pedido.clienteNombre);

    let cuenta = porCliente.get(clave);
    if (cuenta === undefined) {
      cuenta = {
        clienteNombre: pedido.clienteNombre,
        totalCentavos: 0,
        cuantos: 0,
        porciones: 0,
        desde: pedido.fechaEntrega,
        hasta: pedido.fechaEntrega,
        entregados: 0,
        pedidos: [],
      };
      porCliente.set(clave, cuenta);
      escrituras.set(clave, []);
    }
    escrituras.get(clave)?.push(pedido.clienteNombre);

    cuenta.totalCentavos += pedido.totalCentavos;
    cuenta.cuantos += 1;
    cuenta.porciones += pedido.porciones;
    if (pedido.fechaEntrega < cuenta.desde) cuenta.desde = pedido.fechaEntrega;
    if (pedido.fechaEntrega > cuenta.hasta) cuenta.hasta = pedido.fechaEntrega;
    if (pedido.estadoEntrega === 'entregado') cuenta.entregados += 1;
    cuenta.pedidos.push(pedido);
  }

  // Ya con todas sus escrituras a la vista, se decide como se muestra cada uno.
  for (const [clave, cuenta] of porCliente) {
    cuenta.clienteNombre = nombreParaMostrar(escrituras.get(clave) ?? []);
  }

  const clientes = [...porCliente.values()];

  // sort es estable, asi que a igual fecha se conserva el orden de captura.
  for (const cuenta of clientes) {
    cuenta.pedidos.sort((a, b) => a.fechaEntrega.localeCompare(b.fechaEntrega));
  }

  clientes.sort(
    (a, b) =>
      b.totalCentavos - a.totalCentavos ||
      a.clienteNombre.localeCompare(b.clienteNombre, 'es'),
  );

  return {
    clientes,
    totalCentavos: clientes.reduce((s, c) => s + c.totalCentavos, 0),
    cuantos: clientes.reduce((s, c) => s + c.cuantos, 0),
  };
}

// ---------------------------------------------------------------------------
// Dia que se muestra al abrir la pantalla
// ---------------------------------------------------------------------------

/**
 * Elige el dia que conviene mostrar cuando aun no se ha elegido ninguno.
 *
 * Orden de preferencia:
 *   1. La referencia (normalmente hoy), si ese dia tiene pedidos.
 *   2. El proximo dia con pedidos. Es lo que se pregunta por la manana: lo que
 *      falta por entregar, no lo que ya se entrego.
 *   3. El ultimo dia con pedidos. Solo cuando todo quedo atras, para no abrir
 *      la pantalla en blanco teniendo pedidos capturados.
 *   4. La referencia, cuando no hay ningun pedido.
 *
 * No supone que la lista venga ordenada: recorre una sola vez quedandose con el
 * minimo posterior y el maximo anterior.
 */
export function elegirDiaVisible(
  referencia: FechaLocal,
  fechasConPedidos: readonly FechaLocal[],
): FechaLocal {
  let proximo: FechaLocal | null = null;
  let ultimoAnterior: FechaLocal | null = null;

  for (const fecha of fechasConPedidos) {
    if (fecha === referencia) return referencia;

    if (fecha > referencia) {
      if (proximo === null || fecha < proximo) proximo = fecha;
    } else if (ultimoAnterior === null || fecha > ultimoAnterior) {
      ultimoAnterior = fecha;
    }
  }

  return proximo ?? ultimoAnterior ?? referencia;
}
